import { createSlice } from "@reduxjs/toolkit";
import { showLoading, hideLoading } from "react-redux-loading-bar";
import {
  updatePayout,
  updatePayoutStatus,
  downloadPayoutReceipt,
  getAllPayouts,
  uploadPayoutFile,
  requestPayoutOtp,
  transferPayout,
  getPayoutOverview,
  downloadPayouts,
  bulkTransferPreview,
} from "../../../api/payoutAPI";
import moment from "moment/moment";
import { saveAs } from "file-saver";
import { addFilter } from "../../../app/utils";

export const payoutSlice = createSlice({
  name: "payout",
  initialState: {
    payouts: [],
    pagination: {},
    page: 0,
    rowsPerPage: 100,
    sort: "desc",
    sortBy: "payoutDate",
    selectedIds: [],
    searchValue: "",
    selectedYearMonth: moment().subtract(1, "month").format("YYYY-MM"),
    selectedAuditLogs: [],
    selectedCommentLogs: [],
    selectedPayout: null,
    selectedFilter: {
      status: null,
      isB2b: "false",
      country: "Malaysia",
      financeCheck: null,
    },
    selectedPayoutAmounts: {},
    overview: null,
    preview: null,
    auditLogsModalOpen: false,
    commentLogsModalOpen: false,
    uploadPayoutModalOpen: false,
    transferPayoutModalOpen: false,
    monthSelectionModalOpen: false,
    editModalOpen: false,
    filterModalOpen: false,
    bulkTransferModalOpen: false,
    successMessage: "",
    errorMessage: "",
  },
  reducers: {
    setSelectedIds: (state, action) => {
      state.selectedIds = action.payload;
      const amounts = {};
      action.payload.forEach((id) => {
        amounts[id] = state.selectedPayoutAmounts[id] ?? 0;
      });
      state.payouts.forEach((payout) => {
        if (action.payload.includes(payout.id)) {
          amounts[payout.id] = payout.disbursedAmount ?? 0;
        }
      });
      state.selectedPayoutAmounts = amounts;
    },
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setRowsPerPage: (state, action) => {
      state.rowsPerPage = action.payload;
    },
    setPagination: (state, action) => {
      state.pagination = action.payload;
    },
    setSort: (state, action) => {
      state.sort = action.payload;
    },
    setSortBy: (state, action) => {
      state.sortBy = action.payload;
    },
    setPayouts: (state, action) => {
      state.payouts = action.payload;
      action.payload.forEach((payout) => {
        if (state.selectedIds.includes(payout.id)) {
          state.selectedPayoutAmounts[payout.id] = payout.disbursedAmount ?? 0;
        }
      });
    },
    setSearchValue: (state, action) => {
      state.searchValue = action.payload;
    },
    setSelectedYearMonth: (state, action) => {
      state.selectedYearMonth = action.payload;
    },
    setSelectedAuditLogs: (state, action) => {
      state.selectedAuditLogs = action.payload;
    },
    setSelectedCommentLogs: (state, action) => {
      state.selectedCommentLogs = action.payload;
    },
    setSelectedPayout: (state, action) => {
      state.selectedPayout = action.payload;
    },
    setSelectedFilter: (state, action) => {
      state.selectedFilter = action.payload;
    },
    setOverview: (state, action) => {
      state.overview = action.payload;
    },
    setPreview: (state, action) => {
      state.preview = action.payload;
    },
    showAuditLogsModal: (state, action) => {
      state.auditLogsModalOpen = action.payload;
    },
    showCommentLogsModal: (state, action) => {
      state.commentLogsModalOpen = action.payload;
    },
    showUploadPayoutModal: (state, action) => {
      state.uploadPayoutModalOpen = action.payload;
    },
    showTransferPayoutModal: (state, action) => {
      state.transferPayoutModalOpen = action.payload;
    },
    showMonthSelectionModal: (state, action) => {
      state.monthSelectionModalOpen = action.payload;
    },
    showEditModal: (state, action) => {
      state.editModalOpen = action.payload;
    },
    showFilterModal: (state, action) => {
      state.filterModalOpen = action.payload;
    },
    showBulkTransferModal: (state, action) => {
      state.bulkTransferModalOpen = action.payload;
    },
    setSuccessMessage: (state, action) => {
      state.successMessage = action.payload;
    },
    setErrorMessage: (state, action) => {
      state.errorMessage = action.payload;
    },
  },
});

export const {
  setSelectedCommentLogs,
  setSelectedPayout,
  showEditModal,
  showAuditLogsModal,
  showCommentLogsModal,
  setSelectedAuditLogs,
  setSearchValue,
  setSelectedYearMonth,
  showMonthSelectionModal,
  showTransferPayoutModal,
  showUploadPayoutModal,
  showBulkTransferModal,
  setPreview,
  setSelectedIds,
  setPage,
  setRowsPerPage,
  setPagination,
  setSort,
  setSortBy,
  setPayouts,
  setSuccessMessage,
  setErrorMessage,
  setSelectedFilter,
  showFilterModal,
  setOverview,
} = payoutSlice.actions;

export const selectState = (state) => state.payout;

export const handleGetPayouts = () => async (dispatch, getState) => {
  dispatch(showLoading());
  const state = getState();

  try {
    let filterParamsArray = [];
    const { selectedYearMonth, selectedFilter } = state.payout;

    addFilter(filterParamsArray, "yearMonth", selectedYearMonth);
    addFilter(filterParamsArray, "status", selectedFilter.status);
    addFilter(filterParamsArray, "isB2b", selectedFilter.isB2b);
    addFilter(filterParamsArray, "country", selectedFilter.country);
    addFilter(filterParamsArray, "financeCheck", selectedFilter.financeCheck);
    let filterParams = filterParamsArray.join(",");

    const response = await getAllPayouts(
      state.payout.page,
      state.payout.rowsPerPage,
      state.payout.sort,
      state.payout.sortBy,
      filterParams,
      state.payout.searchValue
    );
    const { content, ...pagination } = response.data;

    dispatch(setPagination(pagination));
    dispatch(setPayouts(content));
    dispatch(showMonthSelectionModal(false));
  } catch (error) {
    dispatch(setErrorMessage(error.response?.data?.message ?? error.message));
    dispatch(setPayouts([]));
  } finally {
    dispatch(hideLoading());
  }
};

export const handleDownloadPayoutReceipt =
  (id, yearMonth) => async (dispatch, getState) => {
    dispatch(showLoading());

    try {
      const response = await downloadPayoutReceipt(id);
      let filename = `payout-receipt-${id}.pdf`;
      saveAs(new Blob([response.data], { type: "application/pdf" }), filename);
      dispatch(setSuccessMessage("Successfully download payout receipt!"));
    } catch (error) {
      dispatch(setErrorMessage(error.response?.data?.message ?? error.message));
    } finally {
      dispatch(hideLoading());
    }
  };

export const handleUploadPayoutFile =
  (passCode, yearMonth, file) => async (dispatch) => {
    dispatch(showLoading());
    try {
      const [year, month] = yearMonth.split("-");
      const formData = new FormData();
      formData.append("csvFile", file, file.name);
      formData.append("year", year);
      formData.append("month", month);
      formData.append("passcode", passCode);
      formData.append("overwrite", true);

      await uploadPayoutFile(formData);
      dispatch(showUploadPayoutModal(false));
      dispatch(setSuccessMessage("Successfully uploaded payout file!"));
      dispatch(handleGetPayouts());
      dispatch(handleGetPayoutOverview());
    } catch (error) {
      dispatch(
        setErrorMessage(
          error.response?.data?.message ?? error.message + " (Unauthorized)"
        )
      );
    } finally {
      dispatch(hideLoading());
    }
  };

export const handleRequestOtp = (authorizer) => async (dispatch, getState) => {
  dispatch(showLoading());
  const state = getState();

  try {
    await requestPayoutOtp(authorizer, state.payout.selectedIds);
  } catch (error) {
    dispatch(setErrorMessage(error.response?.data?.message ?? error.message));
  } finally {
    dispatch(hideLoading());
  }
};

export const handleTransferPayout = (otp) => async (dispatch, getState) => {
  dispatch(showLoading());
  const state = getState();

  try {
    await transferPayout(otp, state.payout.selectedIds);
    dispatch(showTransferPayoutModal(false));
    dispatch(setSuccessMessage("Successfully sent transfer payout request!"));
    dispatch(setSelectedIds([]));
    dispatch(handleGetPayouts());
    dispatch(handleGetPayoutOverview());
  } catch (error) {
    dispatch(setErrorMessage(error.response?.data?.message ?? error.message));
  } finally {
    dispatch(hideLoading());
  }
};

export const handleUpdateStatus = (id) => async (dispatch, getState) => {
  dispatch(showLoading());

  try {
    await updatePayoutStatus(id);
    dispatch(setSuccessMessage("Successfully update payout status!"));
    dispatch(handleGetPayouts());
    dispatch(handleGetPayoutOverview());
  } catch (error) {
    dispatch(setErrorMessage(error.response?.data?.message ?? error.message));
  } finally {
    dispatch(hideLoading());
  }
};

export const handleUpdatePayout = (id, payout) => async (dispatch) => {
  dispatch(showLoading());

  try {
    await updatePayout(id, payout);
    dispatch(showEditModal(false));
    dispatch(setSuccessMessage("Successfully update payout!"));
    dispatch(handleGetPayouts());
    dispatch(handleGetPayoutOverview());
  } catch (error) {
    dispatch(setErrorMessage(error.response?.data?.message ?? error.message));
  } finally {
    dispatch(hideLoading());
  }
};

export const handleGetPayoutOverview = () => async (dispatch, getState) => {
  dispatch(showLoading());
  const state = getState();

  try {
    const dateParts = state.payout.selectedYearMonth.split("-");
    const year = parseInt(dateParts[0], 10);
    const month = parseInt(dateParts[1], 10);

    const response = await getPayoutOverview(year, month);
    dispatch(setOverview(response.data));
  } catch (error) {
    dispatch(setErrorMessage(error.response?.data?.message ?? error.message));
  } finally {
    dispatch(hideLoading());
  }
};

export const handleDownloadPayout = () => async (dispatch, getState) => {
  dispatch(showLoading());
  const state = getState();
  try {
    const dateParts = state.payout.selectedYearMonth.split("-");
    const year = parseInt(dateParts[0], 10);
    const month = parseInt(dateParts[1], 10);
    const response = await downloadPayouts(year, month);
    let filename = `payout-report-${state.payout.selectedYearMonth}.csv`;
    saveAs(new Blob([response.data], { type: "text/csv" }), filename);
    dispatch(setSuccessMessage("Successfully download payouts!"));
  } catch (error) {
    dispatch(setErrorMessage(error.response?.data?.message ?? error.message));
  } finally {
    dispatch(hideLoading());
  }
};

export const handleGetBulkTransferPreview =
  () => async (dispatch, getState) => {
    dispatch(showLoading());
    const state = getState();
    try {
      const dateParts = state.payout.selectedYearMonth.split("-");
      const year = parseInt(dateParts[0], 10);
      const month = parseInt(dateParts[1], 10);
      const response = await bulkTransferPreview(year, month);
      dispatch(setPreview(response.data));
    } catch (error) {
      dispatch(setErrorMessage(error.response?.data?.message ?? error.message));
    } finally {
      dispatch(hideLoading());
    }
  };

export default payoutSlice.reducer;
